import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface QuotePDFData {
  quoteNumber: string;
  issueDate: Date;
  validUntil: Date;
  acceptedDate?: Date;
  client: {
    name: string;
    email: string;
    phone?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
  };
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    taxRate: number;
    total: number;
  }>;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  total: number;
  notes?: string;
  terms?: string;
  status: string;
}

export const generateQuotePDF = async (data: QuotePDFData, currencySymbol: string = 'R'): Promise<jsPDF> => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 25;
  const contentWidth = pageWidth - (margin * 2);

  // Modern color scheme
  const primaryColor = [59, 130, 246]; // Blue
  const secondaryColor = [107, 114, 128]; // Gray
  const accentColor = [34, 197, 94]; // Green
  const warningColor = [245, 158, 11]; // Amber
  const lightGray = [249, 250, 251];
  const borderColor = [229, 231, 235];

  // Header with modern design
  pdf.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  pdf.rect(0, 0, pageWidth, 45, 'F');
  
  // Company branding
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  pdf.text('C-Band', margin, 28);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Professional Invoicing & Accounting', margin, 38);

  // Quote title (right side)
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('QUOTE', pageWidth - margin - 35, 28);
  
  // Reset text color for content
  pdf.setTextColor(0, 0, 0);

  // Quote details box with modern design
  const detailsBoxY = 55;
  const detailsBoxWidth = 90;
  const detailsBoxHeight = 40;
  
  pdf.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
  pdf.roundedRect(pageWidth - margin - detailsBoxWidth, detailsBoxY, detailsBoxWidth, detailsBoxHeight, 3, 3, 'F');
  pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
  pdf.roundedRect(pageWidth - margin - detailsBoxWidth, detailsBoxY, detailsBoxWidth, detailsBoxWidth, 3, 3, 'S');
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Quote Details', pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 8);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text(`#${data.quoteNumber}`, pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 18);
  pdf.text(`Date: ${data.issueDate.toLocaleDateString()}`, pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 25);
  pdf.text(`Valid: ${data.validUntil.toLocaleDateString()}`, pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 32);
  
  if (data.acceptedDate) {
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text(`Accepted: ${data.acceptedDate.toLocaleDateString()}`, pageWidth - margin - detailsBoxWidth + 5, detailsBoxY + 39);
    pdf.setTextColor(0, 0, 0);
  }

  // Status badge
  const statusX = pageWidth - margin - detailsBoxWidth + 5;
  const statusY = detailsBoxY + 45;
  const statusWidth = 25;
  const statusHeight = 8;
  
  // Color based on status
  let statusColor = secondaryColor;
  if (data.status === 'accepted') statusColor = accentColor;
  else if (data.status === 'pending') statusColor = warningColor;
  else if (data.status === 'expired') statusColor = [239, 68, 68]; // Red
  
  pdf.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
  pdf.roundedRect(statusX, statusY, statusWidth, statusHeight, 2, 2, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.text(data.status.toUpperCase(), statusX + 2, statusY + 5);
  pdf.setTextColor(0, 0, 0);

  // Client information section
  const clientY = 55;
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Bill To:', margin, clientY);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text(data.client.name, margin, clientY + 12);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(data.client.email, margin, clientY + 20);
  
  if (data.client.phone) {
    pdf.text(data.client.phone, margin, clientY + 28);
  }
  
  if (data.client.address) {
    pdf.text(data.client.address.street, margin, clientY + 36);
    pdf.text(`${data.client.address.city}, ${data.client.address.state} ${data.client.address.zipCode}`, margin, clientY + 44);
    pdf.text(data.client.address.country, margin, clientY + 52);
  }

  // Items table with modern styling
  const tableY = 130;
  const colWidths = [85, 25, 25, 25, 25];
  const colX = [margin, margin + 85, margin + 110, margin + 135, margin + 160];

  // Table header with modern design
  pdf.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  pdf.roundedRect(margin, tableY - 12, contentWidth, 12, 2, 2, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Description', colX[0] + 2, tableY - 4);
  pdf.text('Qty', colX[1] + 2, tableY - 4);
  pdf.text('Price', colX[2] + 2, tableY - 4);
  pdf.text('Tax %', colX[3] + 2, tableY - 4);
  pdf.text('Total', colX[4] + 2, tableY - 4);

  // Table content with alternating colors
  pdf.setTextColor(0, 0, 0);
  let currentY = tableY + 5;
  
  data.items.forEach((item, index) => {
    // Check if we need a new page
    if (currentY > pageHeight - 100) {
      pdf.addPage();
      currentY = 30;
    }

    // Alternating row colors
    if (index % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, currentY - 3, contentWidth, 10, 'F');
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    
    // Item description (wrapped)
    const description = item.description;
    const maxWidth = 80;
    const lines = pdf.splitTextToSize(description, maxWidth);
    
    lines.forEach((line: string, lineIndex: number) => {
      pdf.text(line, colX[0] + 2, currentY + (lineIndex * 3));
    });
    
    const descriptionHeight = Math.max(lines.length * 3, 8);
    
    // Other columns
    pdf.text(item.quantity.toString(), colX[1] + 2, currentY + 2);
         pdf.text(`${currencySymbol}${item.unitPrice.toFixed(2)}`, colX[2] + 2, currentY + 2);
    pdf.text(`${item.taxRate}%`, colX[3] + 2, currentY + 2);
    pdf.text(`${currencySymbol}${item.total.toFixed(2)}`, colX[4] + 2, currentY + 2);
    
    currentY += descriptionHeight + 2;
    
    // Add subtle separator line
    if (index < data.items.length - 1) {
      pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 2;
    }
  });

  // Totals section with modern design
  const totalsY = currentY + 15;
  const totalsBoxWidth = 110;
  const totalsBoxHeight = 70;
  
  pdf.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
  pdf.roundedRect(pageWidth - margin - totalsBoxWidth, totalsY, totalsBoxWidth, totalsBoxHeight, 3, 3, 'F');
  pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
  pdf.roundedRect(pageWidth - margin - totalsBoxWidth, totalsY, totalsBoxWidth, totalsBoxHeight, 3, 3, 'S');
  
  const totalsX = pageWidth - margin - totalsBoxWidth + 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Subtotal:', totalsX, totalsY + 15);
  pdf.text(`${currencySymbol}${data.subtotal.toFixed(2)}`, pageWidth - margin - 10, totalsY + 15);
  
  if (data.discountAmount > 0) {
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('Discount:', totalsX, totalsY + 25);
    pdf.text(`-${currencySymbol}${data.discountAmount.toFixed(2)}`, pageWidth - margin - 10, totalsY + 25);
    pdf.setTextColor(0, 0, 0);
  }
  
  pdf.text('Tax:', totalsX, totalsY + (data.discountAmount > 0 ? 35 : 25));
  pdf.text(`${currencySymbol}${data.taxAmount.toFixed(2)}`, pageWidth - margin - 10, totalsY + (data.discountAmount > 0 ? 35 : 25));
  
  pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
  pdf.line(totalsX, totalsY + (data.discountAmount > 0 ? 40 : 30), pageWidth - margin - 10, totalsY + (data.discountAmount > 0 ? 40 : 30));
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text('Total:', totalsX, totalsY + (data.discountAmount > 0 ? 55 : 45));
  pdf.text(`${currencySymbol}${data.total.toFixed(2)}`, pageWidth - margin - 10, totalsY + (data.discountAmount > 0 ? 55 : 45));

  // Notes section
  if (data.notes) {
    const notesY = totalsY + totalsBoxHeight + 20;
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Notes:', margin, notesY);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const notesLines = pdf.splitTextToSize(data.notes, contentWidth - 20);
    notesLines.forEach((line: string, index: number) => {
      pdf.text(line, margin + 5, notesY + 8 + (index * 4));
    });
  }

  // Terms section
  if (data.terms) {
    const termsY = totalsY + totalsBoxHeight + (data.notes ? 35 : 20);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Terms & Conditions:', margin, termsY);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const termsLines = pdf.splitTextToSize(data.terms, contentWidth - 20);
    termsLines.forEach((line: string, index: number) => {
      pdf.text(line, margin + 5, termsY + 8 + (index * 4));
    });
  }

  // Modern footer
  const footerY = pageHeight - 25;
  pdf.setFontSize(9);
  pdf.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
  pdf.setFont('helvetica', 'normal');
  
  // Footer line
  pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
  pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
  
  pdf.text('Thank you for considering our services!', pageWidth / 2, footerY, { align: 'center' });
  pdf.setFontSize(8);
  pdf.text('C-Band - Professional Invoicing & Accounting System', pageWidth / 2, footerY + 5, { align: 'center' });
  pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, footerY + 10, { align: 'center' });

  return pdf;
};

export const downloadQuotePDF = async (data: QuotePDFData, filename?: string, currencySymbol?: string): Promise<void> => {
  try {
    const pdf = await generateQuotePDF(data, currencySymbol);
    const defaultFilename = `quote-${data.quoteNumber}-${data.issueDate.toISOString().split('T')[0]}.pdf`;
    pdf.save(filename || defaultFilename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF');
  }
};

export const generateQuoteHTML = (data: QuotePDFData): string => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Quote ${data.quoteNumber}</title>
      <style>
        body {
          font-family: 'Helvetica', 'Arial', sans-serif;
          margin: 0;
          padding: 20px;
          background: white;
          color: #1f2937;
        }
        .header {
          background: #3b82f6;
          color: white;
          padding: 20px;
          margin: -20px -20px 20px -20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .company-info h1 {
          margin: 0;
          font-size: 24px;
          font-weight: bold;
        }
        .company-info p {
          margin: 5px 0 0 0;
          font-size: 12px;
          opacity: 0.9;
        }
        .quote-title {
          text-align: right;
        }
        .quote-title h2 {
          margin: 0;
          font-size: 20px;
        }
        .quote-details {
          font-size: 10px;
          margin-top: 10px;
        }
        .quote-details p {
          margin: 2px 0;
        }
        .client-section {
          margin: 30px 0;
        }
        .client-section h3 {
          margin: 0 0 10px 0;
          font-size: 14px;
          font-weight: bold;
        }
        .client-info h4 {
          margin: 0 0 5px 0;
          font-size: 12px;
          font-weight: bold;
        }
        .client-info p {
          margin: 2px 0;
          font-size: 11px;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        .items-table th {
          background: #f3f4f6;
          padding: 8px;
          text-align: left;
          font-size: 10px;
          font-weight: bold;
          border-bottom: 1px solid #e5e7eb;
        }
        .items-table td {
          padding: 8px;
          border-bottom: 1px solid #f3f4f6;
          font-size: 10px;
        }
        .totals-section {
          margin: 20px 0;
          text-align: right;
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          margin: 5px 0;
          font-size: 11px;
        }
        .total-row.total {
          font-weight: bold;
          font-size: 12px;
          border-top: 1px solid #e5e7eb;
          padding-top: 10px;
          margin-top: 10px;
        }
        .notes-section, .terms-section {
          margin: 20px 0;
        }
        .notes-section h3, .terms-section h3 {
          margin: 0 0 10px 0;
          font-size: 12px;
          font-weight: bold;
        }
        .notes-section p, .terms-section p {
          margin: 0;
          font-size: 10px;
          line-height: 1.4;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 8px;
          color: #6b7280;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-info">
          <h1>C-Band</h1>
          <p>Professional Invoicing & Accounting</p>
          <p>info@c-band.com</p>
        </div>
        <div class="quote-title">
          <h2>QUOTE</h2>
          <div class="quote-details">
            <p><strong>Quote #:</strong> ${data.quoteNumber}</p>
            <p><strong>Date:</strong> ${data.issueDate.toLocaleDateString()}</p>
            <p><strong>Valid Until:</strong> ${data.validUntil.toLocaleDateString()}</p>
            ${data.acceptedDate ? `<p><strong>Accepted:</strong> ${data.acceptedDate.toLocaleDateString()}</p>` : ''}
          </div>
        </div>
      </div>

      <div class="client-section">
        <h3>Bill To:</h3>
        <div class="client-info">
          <h4>${data.client.name}</h4>
          <p>${data.client.email}</p>
          ${data.client.phone ? `<p>${data.client.phone}</p>` : ''}
          ${data.client.address ? `
            <p>${data.client.address.street}</p>
            <p>${data.client.address.city}, ${data.client.address.state} ${data.client.address.zipCode}</p>
            <p>${data.client.address.country}</p>
          ` : ''}
        </div>
      </div>

      <table class="items-table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Tax Rate</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          ${data.items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td>${item.quantity}</td>
                                                             <td>${item.unitPrice.toFixed(2)}</td>
                 <td>${item.taxRate}%</td>
                 <td>${item.total.toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="totals-section">
        <div class="total-row">
          <span>Subtotal:</span>
                      <span>${data.subtotal.toFixed(2)}</span>
          </div>
          ${data.discountAmount > 0 ? `
          <div class="total-row">
            <span>Discount:</span>
            <span>-${data.discountAmount.toFixed(2)}</span>
          </div>
          ` : ''}
          <div class="total-row">
            <span>Tax:</span>
            <span>${data.taxAmount.toFixed(2)}</span>
          </div>
          <div class="total-row total">
            <span>Total:</span>
            <span>${data.total.toFixed(2)}</span>
          </div>
      </div>

      ${data.notes ? `
        <div class="notes-section">
          <h3>Notes:</h3>
          <p>${data.notes}</p>
        </div>
      ` : ''}

      ${data.terms ? `
        <div class="terms-section">
          <h3>Terms & Conditions:</h3>
          <p>${data.terms}</p>
        </div>
      ` : ''}

      <div class="footer">
        <p>C-Band - Professional Invoicing & Accounting System</p>
        <p>Generated on ${new Date().toLocaleDateString()}</p>
      </div>
    </body>
    </html>
  `;
};

export const generateQuotePDFFromHTML = async (data: QuotePDFData): Promise<jsPDF> => {
  const html = generateQuoteHTML(data);
  
  // Create a temporary div to render the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '0';
  document.body.appendChild(tempDiv);

  try {
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf;
  } finally {
    document.body.removeChild(tempDiv);
  }
};
